import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { QrCode, Download, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewQRCode = ({ userType, userId }) => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchQRCode();
  }, [userType, userId]);

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/reviews/qr/${userType}/${userId}`);
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
      toast.error('Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrData?.qr_code) return;

    const link = document.createElement('a');
    link.href = qrData.qr_code;
    link.download = `review-qr-code-${userType}-${userId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded');
  };

  const handleCopyLink = () => {
    if (!qrData?.review_url) return;

    navigator.clipboard.writeText(qrData.review_url);
    toast.success('Review link copied to clipboard');
  };

  const handleOpenLink = () => {
    if (!qrData?.review_url) return;
    window.open(qrData.review_url, '_blank');
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <QrCode className="text-[#1A4D2E]" />
        <h3 className="text-lg font-semibold text-stone-900">Review QR Code</h3>
      </div>

      <p className="text-sm text-stone-600 mb-4">
        Share this QR code with your customers. They can scan it to leave a review.
      </p>

      {/* QR Code Image */}
      <div className="bg-white p-4 rounded-lg border-2 border-stone-200 mb-4 flex justify-center">
        {qrData?.qr_code && (
          <img 
            src={qrData.qr_code} 
            alt="Review QR Code" 
            className="w-64 h-64"
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        <Button
          onClick={handleDownload}
          className="w-full bg-[#1A4D2E]"
        >
          <Download size={16} className="mr-2" />
          Download QR Code
        </Button>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleCopyLink}
            variant="outline"
          >
            <Copy size={16} className="mr-2" />
            Copy Link
          </Button>

          <Button
            onClick={handleOpenLink}
            variant="outline"
          >
            <ExternalLink size={16} className="mr-2" />
            Open Link
          </Button>
        </div>
      </div>

      {/* Review URL */}
      <div className="mt-4 p-3 bg-stone-50 rounded text-xs break-all text-stone-600">
        {qrData?.review_url}
      </div>
    </Card>
  );
};

export default ReviewQRCode;
